import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { RelativeTime } from '@/components/common/relative-time';
import { EmptyState } from '@/components/feedback/EmptyState';
import { NoFilterResults, SearchField } from '@/components/filters';
import { PageShell } from '@/components/layout/PageShell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { lookupOptionLabel } from '@/features/odoo/lookup-state';
import { OdooSectionNav } from '@/features/odoo/components/OdooSectionNav';
import {
  OpeningAccountCallout,
  OpeningBalanceErrorPanel,
  OpeningBalancePostedNotice,
} from '@/features/odoo/components/OpeningBalanceNotices';
import { OpeningBalanceTable } from '@/features/odoo/components/OpeningBalanceTable';
import { OpeningBalanceTiles } from '@/features/odoo/components/OpeningBalanceTiles';
import { PostOpeningBalancesDialog } from '@/features/odoo/components/PostOpeningBalancesDialog';
import {
  describeOpeningBalanceError,
  OPENING_BALANCE_PAGE_SIZE,
  OPENING_BALANCE_SUMMARY_FILTERS,
  openingBalanceFiltersFromSearch,
  OpeningBalanceSearchSchema,
  postButtonState,
  postResultParams,
  type OpeningBalanceSearch,
} from '@/features/odoo/opening-balances';
import {
  useOdooLookup,
  useOdooOpeningBalancePreview,
  useOdooPermissions,
  useOdooSettings,
  usePostOdooOpeningBalances,
} from '@/features/odoo/queries';
import type { OpeningBalancePostResult } from '@/features/odoo/types';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useToast } from '@/hooks/use-toast';
import { formatDate, formatDateTimeWithZone } from '@/lib/dates';
import { CheckCircle2, RefreshCw, Send } from '@/lib/icons';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_layout/settings/odoo/opening-balances')({
  validateSearch: OpeningBalanceSearchSchema,
  component: OdooOpeningBalancesPage,
});

function OdooOpeningBalancesPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { canUpdate } = useOdooPermissions();

  const page = search.page ?? 1;
  const filters = openingBalanceFiltersFromSearch(search);
  const [searchInput, setSearchInput] = useState(search.search ?? '');
  const debouncedSearch = useDebouncedValue(searchInput, 350).trim();

  const summary = useOdooOpeningBalancePreview(OPENING_BALANCE_SUMMARY_FILTERS);
  const list = useOdooOpeningBalancePreview(filters);
  const settings = useOdooSettings();
  const accountId = settings.data?.openingBalanceAccountId ?? null;
  const accounts = useOdooLookup('opening-accounts');
  const post = usePostOdooOpeningBalances();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [posted, setPosted] = useState<OpeningBalancePostResult | null>(null);

  const goTo = (next: Partial<OpeningBalanceSearch>) =>
    navigate({
      to: '/settings/odoo/opening-balances',
      search: (prev) => {
        const merged = { ...prev, page: undefined, ...next };
        return Object.fromEntries(
          Object.entries(merged).filter(([, value]) => value !== undefined && value !== '')
        );
      },
      replace: true,
    });

  const lastPushed = useRef(search.search ?? '');
  useEffect(() => {
    const fromUrl = search.search ?? '';
    if (fromUrl === lastPushed.current) return;
    lastPushed.current = fromUrl;
    setSearchInput(fromUrl);
  }, [search.search]);

  useEffect(() => {
    if (debouncedSearch === lastPushed.current) return;
    lastPushed.current = debouncedSearch;
    void goTo({ search: debouncedSearch });
    // goTo is rebuilt each render; only a settled term should touch the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const preview = summary.data;
  const count = summary.isError ? null : (preview?.totals.contracts ?? null);
  const accountSet = settings.data ? accountId !== null : null;
  const button = postButtonState({
    canUpdate,
    count,
    accountSet,
    pending: post.isPending,
  });

  const accountLabel = (() => {
    if (accountId === null) return null;
    const item = accounts.data?.find((a) => a.id === accountId);
    return item ? lookupOptionLabel(item) : t('odoo.lookup.storedId', { id: accountId });
  })();

  const refresh = () => {
    void summary.refetch();
    void list.refetch();
  };

  const openDialog = () => {
    post.reset();
    setDialogOpen(true);
  };

  const handlePost = (confirmCount: number) => {
    post.mutate(
      { confirmCount },
      {
        onSuccess: (result) => {
          setDialogOpen(false);
          setPosted(result);
          toast({
            title: t('odoo.openingBalances.post.success.title', { count: result.enqueued }),
            description: t('odoo.openingBalances.post.success.body', postResultParams(result)),
          });
        },
        onError: (error) => {
          if (describeOpeningBalanceError(error).refreshPreview) refresh();
        },
      }
    );
  };

  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / OPENING_BALANCE_PAGE_SIZE));
  const firstRow = total === 0 ? 0 : (page - 1) * OPENING_BALANCE_PAGE_SIZE + 1;
  const lastRow = Math.min(page * OPENING_BALANCE_PAGE_SIZE, total);
  const isFiltered = Boolean(search.search);
  const nothingToPost = preview?.totals.contracts === 0;

  return (
    <PageShell
      title={t('odoo.openingBalances.title')}
      description={t('odoo.openingBalances.description')}
      actions={
        <>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={refresh}
            aria-label={t('odoo.sync.refresh')}
            title={t('odoo.sync.refresh')}
          >
            <RefreshCw
              className={cn('h-4 w-4', (summary.isFetching || list.isFetching) && 'animate-spin')}
              aria-hidden
            />
          </Button>
          {button.visible ? (
            <Button
              type="button"
              size="sm"
              disabled={!button.enabled}
              onClick={openDialog}
              title={button.reasonKey ? t(button.reasonKey) : undefined}
              aria-describedby={button.reasonKey ? 'opening-post-reason' : undefined}
            >
              <Send className="h-4 w-4" aria-hidden />
              {t('odoo.openingBalances.post.action')}
            </Button>
          ) : null}
          {button.reasonKey ? (
            <span id="opening-post-reason" className="sr-only">
              {t(button.reasonKey)}
            </span>
          ) : null}
        </>
      }
    >
      <OdooSectionNav current="openingBalances" />

      {preview ? (
        <dl
          className="flex flex-wrap gap-x-8 gap-y-2 text-sm"
          data-testid="opening-dates"
        >
          <div className="flex gap-2">
            <dt className="text-muted-foreground">{t('odoo.openingBalances.goLive')}</dt>
            <dd className="font-medium">{formatDateTimeWithZone(preview.asOf)}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-muted-foreground">{t('odoo.openingBalances.openingDate')}</dt>
            <dd className="font-medium">{formatDate(preview.openingDate)}</dd>
          </div>
          {accountLabel ? (
            <div className="flex gap-2">
              <dt className="text-muted-foreground">{t('odoo.openingBalances.account.label')}</dt>
              <dd className="font-medium">{accountLabel}</dd>
            </div>
          ) : null}
        </dl>
      ) : summary.isLoading ? (
        <Skeleton className="h-5 w-80" />
      ) : null}

      {posted ? <OpeningBalancePostedNotice result={posted} /> : null}

      {settings.data && accountId === null && !summary.isError ? (
        <OpeningAccountCallout canUpdate={canUpdate} />
      ) : null}

      {summary.isError ? (
        <OpeningBalanceErrorPanel error={summary.error} onRetry={refresh} />
      ) : nothingToPost ? (
        <section className="rounded-lg border border-border-light bg-card shadow-xs">
          <EmptyState
            icon={<CheckCircle2 className="h-6 w-6" />}
            title={t('odoo.openingBalances.empty.nothingTitle')}
            description={t('odoo.openingBalances.empty.nothingBody')}
          />
        </section>
      ) : (
        <>
          <OpeningBalanceTiles preview={preview} isLoading={summary.isLoading} />

          <section className="overflow-hidden rounded-lg border border-border-light bg-card shadow-xs">
            <div className="space-y-2 border-b border-border-light bg-surface-container-low/60 px-4 py-3 md:px-5 md:py-3.5">
              <div className="flex flex-wrap items-center gap-3">
                <SearchField
                  value={searchInput}
                  onChange={setSearchInput}
                  placeholder={t('odoo.openingBalances.searchPlaceholder')}
                  isPending={searchInput.trim() !== debouncedSearch}
                  className="min-w-[14rem] max-w-md"
                />
                {list.dataUpdatedAt ? (
                  <span className="ms-auto text-xs text-muted-foreground" aria-live="off">
                    {t('odoo.sync.updated')} <RelativeTime value={list.dataUpdatedAt} />
                  </span>
                ) : null}
              </div>
              <p className="text-[13px] text-muted-foreground">
                {t('odoo.openingBalances.formula')}
              </p>
            </div>

            {list.isLoading ? (
              <div className="space-y-2 p-4" aria-busy="true">
                {Array.from({ length: 5 }, (_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : list.isError ? (
              <div className="p-4">
                <OpeningBalanceErrorPanel error={list.error} onRetry={refresh} />
              </div>
            ) : rows.length === 0 ? (
              <div className="p-4">
                <NoFilterResults
                  isFiltered={isFiltered}
                  emptyTitle={t('odoo.openingBalances.empty.nothingTitle')}
                  onReset={() => {
                    setSearchInput('');
                    void navigate({
                      to: '/settings/odoo/opening-balances',
                      search: {},
                      replace: true,
                    });
                  }}
                />
              </div>
            ) : (
              <OpeningBalanceTable rows={rows} />
            )}

            {rows.length > 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border-light px-4 py-3 md:px-5">
                <span className="text-sm text-muted-foreground">
                  {t('odoo.sync.showing', { from: firstRow, to: lastRow, total })}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => void goTo({ page: page - 1 })}
                  >
                    {t('common.actions.previous')}
                  </Button>
                  <Badge variant="outline" size="sm" className="tabular-nums">
                    {page} / {totalPages}
                  </Badge>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => void goTo({ page: page + 1 })}
                  >
                    {t('common.actions.next')}
                  </Button>
                </div>
              </div>
            ) : null}
          </section>
        </>
      )}

      {/* Mounted only while open, so a reopened dialog starts with an empty confirmation. */}
      {preview && dialogOpen ? (
        <PostOpeningBalancesDialog
          open
          onOpenChange={setDialogOpen}
          count={preview.totals.contracts}
          amount={preview.totals.amount}
          openingDate={preview.openingDate}
          isPending={post.isPending}
          error={post.error}
          onSubmit={handlePost}
        />
      ) : null}
    </PageShell>
  );
}
